import { Controller } from '@hotwired/stimulus';

import { fold, foldedRange } from '../utils/fold_text.js';

// Un nom tel qu'on le compare : replié (casse, accents), espaces resserrés.
const normalizeName = (text) =>
  fold(String(text ?? ''))
    .replace(/\s+/g, ' ')
    .trim();

// Le numéro de tête d'un lot de l'agence (« 11 - Électricité » → « Électricité »).
const LEADING_NUMBER = /^\s*\d+\s*-\s*/;

export const withoutNumber = (label) => String(label ?? '').replace(LEADING_NUMBER, '');

// La suggestion que le texte tapé NOMME, ou undefined. Le libellé complet (numéro compris) d'abord, casse
// et accents ignorés ; le nom sans numéro ensuite, et seulement s'il désigne UNE seule suggestion :
// « maçonnerie » face à « 05 - Maçonnerie » et « 12 - Maçonnerie » ne tranche pas au hasard, il reste tel quel.
export const namedSuggestion = (labels, typed) => {
  const wanted = normalizeName(typed);
  if (wanted === '') return undefined;

  const full = labels.find((label) => normalizeName(label) === wanted);
  if (full !== undefined) return full;

  const short = labels.filter((label) => normalizeName(withoutNumber(label)) === wanted);
  return short.length === 1 ? short[0] : undefined;
};

// Hauteur de liste : au moins trois lignes, au plus celle du rendu serveur (`max-h-72`, 288 px).
const MIN_LIST_HEIGHT = 132;
const MAX_LIST_HEIGHT = 288;
const VIEWPORT_MARGIN = 8;

const HIGHLIGHT_ATTRIBUTE = 'data-autocomplete-input-highlight';

/**
 * Autocomplete Input Controller (Form::AutocompleteInputComponent)
 *
 * Champ texte LIBRE avec suggestions (« Nom du lot » du planning). L'input visible est le combobox
 * (pattern ARIA combobox + listbox) et porte lui-même la valeur postée : il n'y a ni champ caché ni id.
 *
 * Targets:
 * - input: le champ (role=combobox), hôte de aria-expanded + aria-activedescendant.
 * - field: l'enveloppe du champ (repère pour placer la liste au-dessus du clavier mobile).
 * - clear: la croix, montrée tant que le champ n'est pas vide.
 * - panel / listbox: la liste sous le champ.
 * - option / optionLabel / checkmark: une suggestion, son texte (seul texte filtré), sa coche.
 * - noMatch: « Aucun … ne correspond. » (présentation).
 * - customOption / customLabel: « Utiliser « … » » et le texte tapé (textContent, jamais innerHTML, #591).
 *
 * Classes: active (ligne active d'une suggestion), customActive (ligne « Utiliser » active),
 * highlight (la <mark> de la partie trouvée).
 *
 * Comportement (maquette « Nom du lot — saisie directe », 16/10) :
 * - Focus ou clic : la liste entière s'ouvre, aucune ligne active (Entrée n'écrase pas un nom déjà là).
 * - Frappe : la liste se filtre (accents ignorés, partie trouvée en gras) ; la 1re ligne visible devient
 *   active. « Utiliser « texte » » reste en bas tant que le texte ne nomme aucune suggestion ; sans
 *   aucune correspondance, elle est seule et active.
 * - ↑/↓ naviguent ; Entrée choisit la ligne active ; Échap ferme (garde en capture : la fermeture ne
 *   remonte pas au tiroir).
 * - Tab ou clic ailleurs GARDENT le texte tapé ; Tab après ↑/↓ prend la ligne que l'on vient de désigner.
 *   Un texte TAPÉ qui nomme une suggestion (`namedSuggestion`) prend son libellé en quittant le champ ;
 *   un nom déjà enregistré que l'on ne retouche pas n'est jamais réécrit.
 * - Croix : vide le champ et rouvre la liste entière, depuis le haut. Pas de chevron (Nicolas, 16/10) : un
 *   clic dans le champ ouvre déjà la liste entière.
 * - Toute la liste retient le focus dans le champ (`keepFocus` sur le panneau) : un clic sur sa barre de
 *   défilement ou sa marge ne la ferme pas.
 * - Mobile : à l'ouverture, la liste est bornée à la place laissée par le clavier (`visualViewport`) et
 *   par le bas de la zone qui défile (le pied du tiroir), et le champ défile en haut s'il en reste trop peu.
 * - Rouverte, la liste montre la suggestion choisie ; ↓ part d'elle.
 */
export default class extends Controller {
  static targets = [
    'input',
    'field',
    'clear',
    'panel',
    'listbox',
    'option',
    'optionLabel',
    'checkmark',
    'noMatch',
    'customOption',
    'customLabel',
  ];

  static classes = ['active', 'customActive', 'highlight'];

  connect() {
    this.filtering = false;
    // Le texte a été TAPÉ depuis la dernière valeur écrite : lui seul se normalise en quittant le champ.
    this.typed = false;
    // ↑/↓ ont désigné une ligne : Tab la prend, comme Entrée.
    this.navigated = false;
    this.activeIndex = -1;
    this.boundEscapeGuard = this.escapeGuard.bind(this);
    this.boundFit = this.fitToViewport.bind(this);
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundEscapeGuard, true);
    window.visualViewport?.removeEventListener('resize', this.boundFit);
  }

  get isOpen() {
    return !this.panelTarget.classList.contains('hidden');
  }

  // ── Ouverture / fermeture ──────────────────────────────────────────────────────────────────────────

  // Focus ou clic dans le champ : la liste ENTIÈRE, aucune ligne active, la suggestion choisie en vue.
  open() {
    if (this.isOpen) return;

    this.filtering = false;
    this.#render();
    this.#show();
    this.#setActive(-1);
    this.#revealChosen();
  }

  // La suggestion qui EST la valeur du champ, ou undefined.
  #chosenOption() {
    return this.optionTargets.find((option) => option.dataset.value === this.inputTarget.value);
  }

  // Fait défiler la LISTE (jamais la page) jusqu'à la suggestion choisie et sa coche (QA 16/10 : au 14ᵉ lot,
  // la liste rouverte montrait « 01 - Démolition » et cachait le choix en cours).
  #revealChosen() {
    const chosen = this.#chosenOption();
    if (!chosen) return;

    const list = this.listboxTarget;
    list.scrollTop = Math.max(0, chosen.offsetTop - (list.clientHeight - chosen.offsetHeight) / 2);
  }

  // `commit` faux seulement après un choix explicite, qui a déjà écrit sa valeur.
  close({ commit = true } = {}) {
    if (commit) this.#commitTyped();
    this.filtering = false;
    this.navigated = false;
    this.#setActive(-1);
    this.#hide();
  }

  // Montre la liste (aucune ligne à montrer : elle reste fermée) sans toucher au filtre.
  #show() {
    if (this.isOpen || !this.#hasVisibleRows()) return;

    this.panelTarget.classList.remove('hidden');
    this.inputTarget.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.boundEscapeGuard, true);
    window.visualViewport?.addEventListener('resize', this.boundFit);
    this.fitToViewport();
  }

  #hide() {
    this.panelTarget.classList.add('hidden');
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.listboxTarget.style.maxHeight = '';
    document.removeEventListener('keydown', this.boundEscapeGuard, true);
    window.visualViewport?.removeEventListener('resize', this.boundFit);
  }

  // La croix : champ vide, liste entière rouverte depuis le haut (QA 16/10 : elle gardait son défilement).
  clear() {
    this.#write('');
    if (document.activeElement !== this.inputTarget) this.inputTarget.focus();
    this.filtering = false;
    if (this.isOpen) {
      this.#render();
      this.#setActive(-1);
    } else {
      this.open();
    }
    this.listboxTarget.scrollTop = 0;
  }

  // Un clic dans la liste ou sur un bouton du champ ne fait pas perdre le focus au champ (sinon `blur`
  // fermerait la liste et validerait le texte avant le choix, ou avant le défilement).
  keepFocus(event) {
    event.preventDefault();
  }

  // Quitter le champ (Tab, clic ailleurs) garde le texte tapé.
  handleBlur() {
    if (this.isOpen) {
      this.close();
    } else {
      this.#commitTyped();
    }
  }

  // Garde d'Échap en CAPTURE tant que la liste est ouverte : elle ferme la liste seule, jamais le tiroir
  // qui contient le formulaire (même contrat que `searchable_select_controller.js#escapeGuard`).
  escapeGuard(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    event.preventDefault();
    this.close();
  }

  // ── Frappe et clavier ──────────────────────────────────────────────────────────────────────────────

  search() {
    this.typed = true;
    this.navigated = false;
    this.#syncClear();
    this.filtering = normalizeName(this.inputTarget.value) !== '';
    this.#render();
    if (this.#hasVisibleRows()) {
      this.#show();
    } else {
      this.#hide();
    }
    // Sur une frappe, la 1re ligne visible est active : le 1er lot qui correspond, sinon « Utiliser ».
    this.#setActive(this.filtering && this.#hasVisibleRows() ? 0 : -1);
  }

  handleKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) this.open();
        this.navigated = true;
        // Sans ligne active, ↓ part de la suggestion choisie (sinon de la première).
        if (this.activeIndex < 0) {
          this.#setActive(Math.max(0, this.#navigableRows().indexOf(this.#chosenOption())));
          return;
        }
        this.#setActive(Math.min(this.activeIndex + 1, this.#navigableRows().length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) return;
        this.navigated = true;
        this.#setActive(Math.max(this.activeIndex - 1, 0));
        break;
      case 'Enter':
        // Liste fermée : Entrée garde son rôle natif (envoyer le formulaire), le texte validé d'abord.
        if (!this.isOpen) {
          this.#commitTyped();
          return;
        }
        event.preventDefault();
        if (this.activeIndex >= 0) {
          this.#navigableRows()[this.activeIndex]?.click();
        } else {
          this.close();
        }
        break;
      // Tab n'est jamais annulé : `blur` valide le texte tapé et ferme la liste. Après ↑/↓, la ligne
      // désignée est choisie d'abord (l'écran la montre comme le choix en cours).
      case 'Tab':
        if (this.isOpen && this.navigated && this.activeIndex >= 0) {
          this.#navigableRows()[this.activeIndex]?.click();
        }
        break;
      default:
        break;
    }
  }

  hover(event) {
    const index = this.#navigableRows().indexOf(event.currentTarget);
    if (index >= 0 && index !== this.activeIndex) this.#setActive(index, { scroll: false });
  }

  // ── Choix ──────────────────────────────────────────────────────────────────────────────────────────

  pick(event) {
    this.#write(event.currentTarget.dataset.value ?? '');
    this.close({ commit: false });
  }

  pickCustom() {
    this.#write(this.inputTarget.value.trim());
    this.close({ commit: false });
  }

  // ── Clavier mobile ─────────────────────────────────────────────────────────────────────────────────

  // Borne la liste à la place visible SOUS le champ (le clavier réduit le `visualViewport`) ; s'il reste
  // moins de trois lignes, fait défiler le champ en haut de son conteneur puis remesure.
  fitToViewport() {
    const viewport = window.visualViewport;
    if (!viewport || !this.isOpen) return;

    let room = this.#roomBelow(viewport);
    if (room < MIN_LIST_HEIGHT) {
      this.fieldTarget.scrollIntoView({ block: 'start' });
      room = this.#roomBelow(viewport);
    }
    const height = Math.max(MIN_LIST_HEIGHT, Math.min(MAX_LIST_HEIGHT, room));
    this.listboxTarget.style.maxHeight = `${height}px`;
  }

  // La place sous le champ, bornée par le bas de l'écran visible ET par celui de la zone qui défile autour
  // du champ (QA 16/10 : dans le tiroir, le pied « Annuler / Créer le lot » recouvrait le bas de la liste).
  #roomBelow(viewport) {
    const bottom = this.fieldTarget.getBoundingClientRect().bottom;
    const limit = Math.min(viewport.offsetTop + viewport.height, this.#scrollerBottom());
    return limit - bottom - VIEWPORT_MARGIN;
  }

  // Le bas du premier ancêtre qui défile (le corps du tiroir), ou l'infini s'il n'y en a pas.
  #scrollerBottom() {
    for (
      let node = this.element.parentElement;
      node && node !== document.body;
      node = node.parentElement
    ) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll')
        return node.getBoundingClientRect().bottom;
    }
    return Infinity;
  }

  // ── Rendu de la liste ──────────────────────────────────────────────────────────────────────────────

  #render() {
    const typed = this.inputTarget.value;
    const query = this.filtering ? fold(typed).trim() : '';
    let matches = 0;

    this.optionTargets.forEach((option) => {
      const label = this.#labelOf(option);
      this.#clearHighlight(label);
      const range = query ? foldedRange(label.textContent, query) : null;
      const shown = query === '' || range !== null;
      option.classList.toggle('hidden', !shown);
      if (range) this.#highlight(label, range);
      if (shown) matches += 1;
    });

    const showCustom = query !== '' && namedSuggestion(this.#labels(), typed) === undefined;
    this.customOptionTarget.classList.toggle('hidden', !showCustom);
    this.customOptionTarget.toggleAttribute('data-separated', showCustom && matches > 0);
    this.customLabelTarget.textContent = showCustom ? typed.trim() : '';
    this.noMatchTarget.classList.toggle('hidden', !(query !== '' && matches === 0));
  }

  #labels() {
    return this.optionTargets.map((option) => option.dataset.value ?? '');
  }

  #hasVisibleRows() {
    return this.#navigableRows().length > 0;
  }

  #navigableRows() {
    const rows = this.optionTargets.filter((option) => !option.classList.contains('hidden'));
    if (!this.customOptionTarget.classList.contains('hidden')) rows.push(this.customOptionTarget);
    return rows;
  }

  #setActive(index, { scroll = true } = {}) {
    const rows = this.#navigableRows();
    this.activeIndex = index;
    [...this.optionTargets, this.customOptionTarget].forEach((row) => {
      const active = rows[index] === row;
      row.setAttribute('aria-selected', String(active));
      const classes =
        row === this.customOptionTarget ? this.customActiveClasses : this.activeClasses;
      classes.forEach((name) => row.classList.toggle(name, active));
    });

    const row = rows[index];
    if (row) {
      this.inputTarget.setAttribute('aria-activedescendant', row.id);
      if (scroll) row.scrollIntoView({ block: 'nearest' });
    } else {
      this.inputTarget.removeAttribute('aria-activedescendant');
    }
  }

  #labelOf(option) {
    return option.querySelector('[data-autocomplete-input-target="optionLabel"]');
  }

  // Entoure [début, fin) du libellé d'une <mark>, construite en nœuds DOM (#591).
  #highlight(label, [start, end]) {
    const text = label.textContent;
    const mark = document.createElement('mark');
    mark.setAttribute(HIGHLIGHT_ATTRIBUTE, '');
    if (this.hasHighlightClass) mark.classList.add(...this.highlightClasses);
    mark.textContent = text.slice(start, end);
    const nodes = [text.slice(0, start), mark, text.slice(end)]
      .filter((node) => node !== '')
      .map((node) => (typeof node === 'string' ? document.createTextNode(node) : node));
    label.replaceChildren(...nodes);
  }

  // Ramène un libellé surligné à UN nœud texte du même texte.
  #clearHighlight(label) {
    if (!label.querySelector(`mark[${HIGHLIGHT_ATTRIBUTE}]`)) return;
    const text = label.textContent;
    label.textContent = text;
  }

  // ── Valeur ─────────────────────────────────────────────────────────────────────────────────────────

  // Valide le texte TAPÉ : la suggestion qu'il nomme prend son libellé, le reste part tel quel. Sans
  // frappe, rien n'est réécrit (un nom enregistré traversé au clavier reste ce qu'il est).
  #commitTyped() {
    if (!this.typed) return;

    const typed = this.inputTarget.value.trim();
    this.#write(namedSuggestion(this.#labels(), typed) ?? typed);
  }

  // La seule écriture de la valeur : champ, croix, coches, et `change` si elle a bougé.
  #write(value) {
    const changed = this.inputTarget.value !== value;
    this.typed = false;
    this.inputTarget.value = value;
    this.#syncClear();
    this.#syncCheckmarks();
    if (changed) this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncClear() {
    this.clearTarget.classList.toggle('hidden', this.inputTarget.value === '');
  }

  #syncCheckmarks() {
    const value = this.inputTarget.value;
    this.optionTargets.forEach((option) => {
      const mark = option.querySelector('[data-autocomplete-input-target="checkmark"]');
      mark?.classList.toggle('hidden', option.dataset.value !== value);
    });
  }
}
